import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/modalSizeSettingsModal.module.scss';
import { ModalKey, ModalSize } from '../../../../libs/ui-settings-manager/interfaces';
import { DEFAULT_MODAL_SIZE, DEFAULT_MODALS_SETTINGS, UISettingsManager } from '../../../../libs/ui-settings-manager/UISettingsManager';
import { BaseModalWindow } from '../../../../components/modalWindows/BaseModalWindow';
import { Checkbox } from 'antd';
import { Button } from '../../../../components/buttons/button';
import { MODAL_REGISTRY } from '../../../../libs/ui-settings-manager/modalRegistry';
interface IModalSizeSettingsModalProps {
    onClose: () => void;
}

const MODAL_KEYS = Object.keys(MODAL_REGISTRY) as ModalKey[];

type Draft = Record<ModalKey, ModalSize>;

const buildDraftFromSettings = (): Draft => {
    const settings = UISettingsManager.getSettings();
    const result = {} as Draft;
    for (const key of MODAL_KEYS) {
        result[key] = settings.modals[key]?.size ?? DEFAULT_MODAL_SIZE;
    }
    return result;
};

export const ModalSizeSettingsModal: FC<IModalSizeSettingsModalProps> = ({ onClose }) => {
    const [draft, setDraft] = useState<Draft>(buildDraftFromSettings);
    const allFullSize = MODAL_KEYS.every((key) => draft[key] === 'fullSize');
    const someFullSize = MODAL_KEYS.some((key) => draft[key] === 'fullSize');

    const allCompactSize = MODAL_KEYS.every((key) => draft[key] === 'compactSize');
    const someCompactSize = MODAL_KEYS.some((key) => draft[key] === 'compactSize');
    const { t } = useTranslation();

    useEffect(() => {
        const handler = () => setDraft(buildDraftFromSettings());
        window.addEventListener('uiSettingsChanged', handler);
        return () => window.removeEventListener('uiSettingsChanged', handler);
    }, []);

    const handleSetSize = (key: ModalKey, size: ModalSize) => {
        setDraft((prev) => ({ ...prev, [key]: size }));
    };

    const handleSetAll = (size: ModalSize) => {
        const next = {} as Draft;
        for (const key of MODAL_KEYS) {
            next[key] = size;
        }
        setDraft(next);
    };

    const handleReset = () => {
        const next = {} as Draft;
        for (const key of MODAL_KEYS) {
            next[key] = DEFAULT_MODALS_SETTINGS[key]?.size ?? DEFAULT_MODAL_SIZE;
        }
        setDraft(next);
    };

    const handleSave = () => {
        const current = UISettingsManager.getSettings();
        const updatedModals = { ...current.modals };
        for (const key of MODAL_KEYS) {
            updatedModals[key] = { size: draft[key] };
        }
        UISettingsManager.updateSection('modals', updatedModals);
        onClose();
    };

    return (
        <BaseModalWindow
            header={t('modals.modalSizeSettings.header')}
            ok={{ onClick: handleSave, title: t('general.save') }}
            cancel={{ onClick: onClose, title: t('general.cancel') }}
            className="modalSizeSettings"
        >
            <div className={styles.container}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.colName}>
                                {t('modals.modalSizeSettings.columns.name')}
                            </th>
                            <th className={styles.colFullSize}>
                                <div className={styles.headerCheck}>
                                    <Checkbox
                                        checked={allFullSize}
                                        indeterminate={someFullSize && !allFullSize}
                                        onChange={(e) => handleSetAll(e.target.checked ? 'fullSize' : 'compactSize')}
                                    />
                                    <span>{t('modals.modalSizeSettings.columns.fullSize')}</span>
                                </div>
                            </th>
                            <th className={styles.colCompactSize}>
                                <div className={styles.headerCheck}>
                                    <Checkbox
                                        checked={allCompactSize}
                                        indeterminate={someCompactSize && !allCompactSize}
                                        onChange={(e) => handleSetAll(e.target.checked ? 'compactSize' : 'fullSize')}
                                    />
                                    <span>{t('modals.modalSizeSettings.columns.compactSize')}</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {MODAL_KEYS.map((key) => {
                            return (
                                <tr key={key} className={styles.row}>
                                    <td className={styles.cellName}>
                                        {t(MODAL_REGISTRY[key].labelKey)}
                                    </td>
                                    <td className={styles.cellCheck}>
                                        <Checkbox
                                            checked={draft[key] === 'fullSize'}
                                            onChange={(e) => handleSetSize(key, e.target.checked ? 'fullSize' : 'compactSize')}
                                        />
                                    </td>
                                    <td className={styles.cellCheck}>
                                        <Checkbox
                                            checked={draft[key] === 'compactSize'}
                                            onChange={(e) => handleSetSize(key, e.target.checked ? 'compactSize' : 'fullSize')}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <Button
                    onClick={handleReset}
                    variant="outprimary"
                >
                    {t('modals.modalSizeSettings.reset')}
                </Button>
            </div>
        </BaseModalWindow>
    );
};